import React, { useState } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import EyeIcon from "@styles/all-images/icons/eye-icon.svg";
import EyeOffIcon from "@styles/all-images/icons/eye-off-icon.svg";
import { logout } from "@/store/slices/authSlice";
import BackIcon from "@styles/all-images/icons/back-button.svg";

interface ChangePasswordProps {
  isModalOpen: boolean;
  onClose: () => void;
}

const ChangePassword = ({ isModalOpen, onClose }: ChangePasswordProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordSchema = Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Password must contain at least one special character",
    );

  const validationSchema = Yup.object({
    currentPassword: Yup.string().required("Current password is required"),
    newPassword: passwordSchema,
    confirmPassword: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("newPassword")], "Passwords must match"),
  });

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("changePassword");

      const response = await dispatch(
        authenticatedCall({
          payload: {
            old_password: values.currentPassword,
            new_password: values.newPassword,
            confirm_password: values.confirmPassword,
          },
          method: "post",
        }),
      );

      if (response.ok) {
        toast.success("Your password has been updated successfully");
        // Logout user and redirect to login
        // Assuming you have a logout action
        await dispatch(logout());
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Unable to update password. Please try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleToggleCurrentPassword = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const handleToggleNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Dialog
      open={isModalOpen}
      maxWidth="sm"
      fullWidth
      classes={{
        paper: "tw-p-6",
      }}
      PaperProps={{
        elevation: 0,
        className: "tw-rounded-lg",
      }}
      className="tw-backdrop-blur-sm tw-bg-black/50"
    >
      <DialogContent className="tw-flex tw-flex-col tw-items-center">
        {/* Back Button */}
        <button
          onClick={onClose}
          className="tw-absolute tw-top-4 tw-left-4 tw-flex tw-items-center tw-gap-2 tw-text-gray-600"
        >
          <img src={BackIcon} alt="Back Icon" className="tw-cursor-pointer" />
          Back
        </button>

        <div className="tw-w-full tw-max-w-md">
          {/* Header Section */}
          <div className="tw-text-center tw-mb-8">
            <img
              src="/logo/Logo.svg"
              alt="Logo"
              className="tw-mx-auto tw-mb-6 tw-max-w-[153px]"
            />
            <h2 className="tw-text-xl tw-font-semibold tw-text-[#232323]">
              Change Password
            </h2>
          </div>

          {/* Form Section */}
          <form onSubmit={formik.handleSubmit} className="tw-space-y-6">
            {/* Current Password Input */}
            <div className="form-group">
              <p className="tw-text-gray-700 tw-mb-2">Current Password</p>
              <TextField
                fullWidth
                name="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                variant="outlined"
                placeholder="Enter current password"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.currentPassword &&
                  Boolean(formik.errors.currentPassword)
                }
                helperText={
                  formik.touched.currentPassword &&
                  formik.errors.currentPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleCurrentPassword}
                        edge="end"
                        className="tw-p-1 tw-border tw-border-gray-300 tw-rounded-full hover:tw-bg-gray-100"
                      >
                        {showCurrentPassword ? (
                          <img src={EyeIcon} alt="EyeIcon" />
                        ) : (
                          <img src={EyeOffIcon} alt="EyeOffIcon" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            {/* New Password Input */}
            <div className="form-group">
              <p className="tw-text-gray-700 tw-mb-2">New Password</p>
              <TextField
                fullWidth
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                variant="outlined"
                placeholder="Enter new password"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.newPassword &&
                  Boolean(formik.errors.newPassword)
                }
                helperText={
                  formik.touched.newPassword && formik.errors.newPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleNewPassword}
                        edge="end"
                        className="tw-p-1 tw-border tw-border-gray-300 tw-rounded-full hover:tw-bg-gray-100"
                      >
                        {showNewPassword ? (
                          <img src={EyeIcon} alt="EyeIcon" />
                        ) : (
                          <img src={EyeOffIcon} alt="EyeOffIcon" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            {/* Confirm Password Input */}
            <div className="form-group">
              <p className="tw-text-gray-700 tw-mb-2">Confirm Password</p>
              <TextField
                fullWidth
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                variant="outlined"
                placeholder="Confirm new password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleConfirmPassword}
                        edge="end"
                        className="tw-p-1 tw-border tw-border-gray-300 tw-rounded-full hover:tw-bg-gray-100"
                      >
                        {showConfirmPassword ? (
                          <img src={EyeIcon} alt="EyeIcon" />
                        ) : (
                          <img src={EyeOffIcon} alt="EyeOffIcon" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            {/* Action Buttons */}
            <div className="tw-flex tw-space-x-4 tw-mt-8">
              <button
                type="button"
                onClick={onClose}
                className="tw-flex-1 tw-h-11 tw-border tw-border-[#146C94] tw-text-[#146C94] tw-px-4 tw-rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="tw-flex-1 tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md disabled:tw-opacity-50"
              >
                {isLoading ? "Updating..." : "Update Password"}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePassword;
